// AI 管理助理的純邏輯層(無 React、無 DOM → 完整可單元測試)。
//
// 這裡放:①導覽白名單解析(navigate 目標 route → adminPlatformModel 顯示名)、
// ②當前頁 page_context 組裝、③config → 可用性判斷、④SSE 事件 fold 進對話泡泡、
// ⑤助理浮窗的 role 可見性閘門。

import { SEARCH_ENTITY_LABEL } from '../../platform/adminPlatformModel';
import { visibleNavItems } from '../../platform/adminNavigation';
import type { AssistantConfig, AssistantEvent, AssistantPageContext } from './assistantApi';

// ── 對話狀態模型 ──────────────────────────────────────────────────────────────

export type ChatRole = 'user' | 'assistant';

/** 助理建議跳頁(navigate 事件 → 渲染成「前往 <label> →」按鈕)。 */
export interface NavAction {
  to: string;
  label: string;
  reason?: string;
  /** 是否命中導覽白名單(未命中則不給點,避免跳到未知路由)。 */
  known: boolean;
}

/** 助理工具進度(tool 事件 → 「查詢中…」細粒度狀態)。 */
export interface ToolActivity {
  name: string;
  status: string;
}

/** 一則對話泡泡。assistant 泡泡在 streaming 期間逐步累積 text / nav / tools。 */
export interface ChatMessage {
  id: string;
  role: ChatRole;
  text: string;
  nav: NavAction[];
  tools: ToolActivity[];
  error?: { code: string; message: string };
  /** true=尚在串流中(顯示游標/停止鈕)。 */
  streaming: boolean;
}

let idSeq = 0;
/** 產生穩定遞增的泡泡 id(避免 render key 碰撞;非安全用途)。 */
export function nextMessageId(prefix = 'm'): string {
  idSeq += 1;
  return `${prefix}-${idSeq}`;
}

export function makeUserMessage(text: string): ChatMessage {
  return { id: nextMessageId('u'), role: 'user', text, nav: [], tools: [], streaming: false };
}

export function makeAssistantMessage(): ChatMessage {
  return { id: nextMessageId('a'), role: 'assistant', text: '', nav: [], tools: [], streaming: true };
}

// ── 導覽白名單:navigate 目標 → 顯示名(adminPlatformModel SSOT) ─────────────

/** route → 導覽項顯示名的對照(自 ADMIN_NAV_GROUPS 攤平,單一 manifest 驅動)。 */
function navLabelIndex(role: string | null): Map<string, string> {
  const map = new Map<string, string>();
  for (const item of visibleNavItems(role)) map.set(item.to, item.label);
  return map;
}

/**
 * 解析助理回的 navigate 目標:
 *  1. 完全命中某導覽項 → 用該項顯示名(known)。
 *  2. 命中實體詳情路由(/users/:id、/devices/:id) → 用 SEARCH_ENTITY_LABEL(known)。
 *  3. 命中某導覽項的子路由(prefix) → 用父項顯示名(known)。
 *  4. 都不中 → known=false、label 退回助理給的 label 或 raw route(不給點)。
 */
export function resolveNavTarget(to: string, providedLabel?: string, role: string | null = 'admin'): NavAction {
  const clean = (to || '').trim();
  const index = navLabelIndex(role);

  // 1. 完全命中
  const exact = index.get(clean);
  if (exact) return { to: clean, label: exact, known: true };

  // 2. 實體詳情路由(/users/:id、/devices/:id、/organizations…)
  const seg = clean.split('/').filter(Boolean); // e.g. ['users','abc']
  if (seg.length >= 2) {
    const entityLabel = SEARCH_ENTITY_LABEL[singularEntity(seg[0])];
    if (entityLabel && index.has(`/${seg[0]}`)) return { to: clean, label: entityLabel, known: true };
  }

  // 3. 子路由 → 找最長的父導覽 prefix
  let best: { to: string; label: string } | null = null;
  for (const [route, label] of index) {
    if (route === '/') continue;
    if (clean === route || clean.startsWith(route + '/')) {
      if (!best || route.length > best.to.length) best = { to: route, label };
    }
  }
  if (best) return { to: clean, label: best.label, known: true };

  // 4. 未知路由
  return { to: clean, label: (providedLabel && providedLabel.trim()) || clean, known: false };
}

/** 路由第一段 → SEARCH_ENTITY_LABEL 的鍵(users→user、devices→device、organizations→org)。 */
function singularEntity(segment: string): string {
  switch (segment) {
    case 'users':
      return 'user';
    case 'devices':
      return 'device';
    case 'organizations':
      return 'org';
    default:
      return segment;
  }
}

// ── 當前頁 page_context ───────────────────────────────────────────────────────

/** pathname 命中哪個導覽項(完全或子路由);回其顯示名。 */
function activeNavLabel(pathname: string, role: string | null): string | undefined {
  let best: { to: string; label: string } | null = null;
  for (const item of visibleNavItems(role)) {
    const route = item.to;
    const hit = route === '/' ? pathname === '/' : pathname === route || pathname.startsWith(route + '/');
    if (hit && (!best || route.length > best.to.length)) best = { to: route, label: item.label };
  }
  return best?.label;
}

/** 組裝喚起助理時要帶的 page_context(當前 route + 顯示名 label)。 */
export function buildPageContext(pathname: string, role: string | null = 'admin'): AssistantPageContext {
  const label = activeNavLabel(pathname, role);
  return label ? { route: pathname, label } : { route: pathname };
}

// ── config → 可用性 ───────────────────────────────────────────────────────────

export interface AssistantAvailability {
  available: boolean;
  /** 不可用時的友善說明(顯示在浮窗、收起輸入框)。 */
  reason?: string;
}

/**
 * 依 config 判斷助理是否可用。`enabled=false`(通常因未設 API key)→ 不可用、回友善說明。
 * config 尚未載入(null)→ 視為不可用但不給錯誤文案(由 UI 顯示載入態)。
 */
export function assistantAvailability(config: AssistantConfig | null): AssistantAvailability {
  if (!config) return { available: false };
  if (config.enabled) return { available: true };
  const reason = config.key_configured
    ? '助理目前未啟用。'
    : '助理未啟用（後端尚未設定 API key）。';
  return { available: false, reason };
}

// ── SSE 事件 fold ─────────────────────────────────────────────────────────────

/** 把單一 SSE 事件 fold 進當前 assistant 泡泡,回傳新泡泡(immutable)。 */
export function reduceAssistantMessage(msg: ChatMessage, ev: AssistantEvent, role: string | null = 'admin'): ChatMessage {
  switch (ev.type) {
    case 'text':
      return { ...msg, text: msg.text + ev.delta };
    case 'navigate':
      return { ...msg, nav: [...msg.nav, { ...resolveNavTarget(ev.to, ev.label, role), reason: ev.reason }] };
    case 'tool': {
      // 同名工具更新狀態,否則新增一列。
      const i = msg.tools.findIndex((t) => t.name === ev.name);
      const tools = i === -1 ? [...msg.tools, { name: ev.name, status: ev.status }] : msg.tools.map((t, j) => (j === i ? { ...t, status: ev.status } : t));
      return { ...msg, tools };
    }
    case 'done':
      return { ...msg, streaming: false };
    case 'error':
      return { ...msg, error: { code: ev.code, message: ev.message }, streaming: false };
    default:
      return msg;
  }
}

// ── role 可見性閘門(僅 UI;伺服器仍為最終強制) ──────────────────────────────

/**
 * 助理浮窗只對 `CanAdminRead` 角色可見:admin / security-officer /
 * platform-owner / cs-readonly / ai-ops。Helpdesk 是獨立執行 plane,
 * helpdesk-agent/helpdesk-lead 與 legacy helpdesk 均不可進 Admin。
 */
export const ASSISTANT_VISIBLE_ROLES = ['admin', 'security-officer', 'platform-owner', 'cs-readonly', 'ai-ops'] as const;

export function canUseAssistant(role: string | null | undefined): boolean {
  return !!role && (ASSISTANT_VISIBLE_ROLES as readonly string[]).includes(role);
}
